import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { ChevronRight, Settings2, Sparkles, Plus, Power, PowerOff } from 'lucide-react';
import { colors } from '../../theme/colors';
import PanelButton from '../PanelButton';
import { apiFetch } from '../../utils/apiUtils';

const FieldSection = styled.div`
  margin-bottom: 2rem; /* 32px - Material Design spacing for distinct content sections */
  
  h4 {
    color: #e0e0e0;
    margin: 0 0 0.5rem 0;
    font-size: 0.875rem;
    font-weight: 600;
  }
`;

const FieldControlExplainer = styled.div`
  color: #a0a0a0;
  font-size: 0.75rem;
  margin-top: 0.5rem;
  line-height: 1.4;
`;

const PriorityDisplay = styled.div`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
`;

const PriorityTimeline = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.isManyDocuments ? '0.25rem' : '0.5rem'};
`;

const DocumentSquare = styled.div`
  width: ${props => props.isManyDocuments ? '36px' : '32px'};
  height: ${props => props.isManyDocuments ? '36px' : '32px'};
  border-radius: ${props => props.isManyDocuments ? '4px' : '6px'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  cursor: help;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: ${props => props.color || '#6b7280'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:hover::after {
    content: '${props => props.fullName}';
    position: absolute;
    top: -35px;
    left: 50%;
    transform: translateX(-50%);
    background: #4a4a4a;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  &:hover::before {
    content: '';
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #4a4a4a;
    z-index: 1000;
  }
`;

const TimelineArrow = styled.div`
  color: #9ca3af;
  display: flex;
  align-items: center;
  margin: 0 ${props => props.isManyDocuments ? '0.0625rem' : '0.25rem'};
`;

const FieldControlRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const FieldsContent = ({
  showImportSection,
  showAddFieldSection,
  defaultDocumentPriority,
  getDocumentKey,
  getDocumentLetter,
  setShowGlobalPriorityModal,
  setShowImportSection,
  setShowAddFieldSection,
  handleEnableAllFields,
  handleDisableAllFields,
  bulkUpdating,
  renderImportSection,
  renderAddFieldSection
}) => {
  const [documentTypeColors, setDocumentTypeColors] = useState({});

  // Fetch document type colors from API
  useEffect(() => {
    const fetchDocumentTypeColors = async () => {
      try {
        const response = await apiFetch('/system-files/document-types');
        // apiFetch already returns parsed JSON
        const data = response;
        if (data.success) {
          const colorMap = {};
          data.document_types.forEach(docType => {
            colorMap[docType.name] = docType.color;
          });
          setDocumentTypeColors(colorMap);
        }
      } catch (error) {
        console.error('Error fetching document type colors:', error);
      }
    };

    fetchDocumentTypeColors();
  }, []);
  // If showing import section, only show that
  if (showImportSection) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        gap: '0.75rem' 
      }}>
        <FieldSection style={{ flexShrink: 0 }}>
          <h4>Quick Define Included Matching Fields</h4>
          <FieldControlExplainer style={{ marginBottom: '0.75rem' }}>
            Enter a list of field names to define which fields should be included in matching. The system will automatically match them to existing fields and enable only these fields for processing.
          </FieldControlExplainer>
        </FieldSection>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {renderImportSection()}
        </div>
      </div>
    );
  }

  // If showing add field section, only show that
  if (showAddFieldSection) {
    return (
      <div>
        <FieldSection>
          <h4>Add Field</h4>
          <FieldControlExplainer style={{ marginBottom: '0.75rem' }}>
            Add a new field to the system
          </FieldControlExplainer>
        </FieldSection>
        {renderAddFieldSection()}
      </div>
    );
  }

  // Default state - show all sections
  return (
    <div>
      <FieldSection>
        <h4>Default Document Priority</h4>
        <PriorityDisplay>
          <div style={{ 
            color: '#a0a0a0', 
            fontSize: '0.75rem', 
            fontWeight: '400', 
            marginBottom: '0.75rem',
            textAlign: 'center',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Current Order
          </div>
          <PriorityTimeline isManyDocuments={defaultDocumentPriority.length > 4}>
            {defaultDocumentPriority.map((documentName, index) => (
              <React.Fragment key={documentName}>
                <DocumentSquare 
                  type={getDocumentKey(documentName)} 
                  fullName={documentName}
                  color={documentTypeColors[documentName]}
                  isManyDocuments={defaultDocumentPriority.length > 4}
                  data-cy="document-priority-preview-tile"
                >
                  {getDocumentLetter(documentName)}
                </DocumentSquare>
                {index < defaultDocumentPriority.length - 1 && (
                  <TimelineArrow isManyDocuments={defaultDocumentPriority.length > 4}>
                    <ChevronRight size={defaultDocumentPriority.length > 4 ? 12 : 16} />
                  </TimelineArrow>
                )}
              </React.Fragment>
            ))}
          </PriorityTimeline>
        </PriorityDisplay>
        <PanelButton onClick={() => setShowGlobalPriorityModal(true)}>
          <Settings2 size={16} />
          Edit Priority Order
        </PanelButton>
      </FieldSection>

      <FieldSection>
        <h4>Quick Define Included Matching Fields</h4>
        <FieldControlExplainer style={{ marginBottom: '0.75rem' }}>
          Enter a list of field names to define which fields should be included in matching. The system will automatically match them to existing fields and enable only these fields for processing.
        </FieldControlExplainer>
        <PanelButton onClick={() => setShowImportSection(true)}>
          <Sparkles size={16} />
          Quick Define Included Fields
        </PanelButton>
      </FieldSection>

      <FieldSection>
        <h4>Add Field</h4>
        <FieldControlExplainer style={{ marginBottom: '0.75rem' }}>
          Add a single new field to the system
        </FieldControlExplainer>
        <PanelButton onClick={() => setShowAddFieldSection(true)}>
          <Plus size={16} />
          Add Field
        </PanelButton>
      </FieldSection>

      <FieldSection>
        <h4>Bulk Field Controls</h4>
        <FieldControlExplainer style={{ marginBottom: '0.75rem' }}>
          Quickly enable or disable all fields for document matching across the entire system
        </FieldControlExplainer>
        <FieldControlRow>
          <PanelButton 
            onClick={handleEnableAllFields}
            disabled={bulkUpdating}
          >
            <Power size={16} />
            {bulkUpdating ? 'Updating...' : 'Enable All'}
          </PanelButton>
          <PanelButton 
            onClick={handleDisableAllFields}
            disabled={bulkUpdating}
          >
            <PowerOff size={16} />
            {bulkUpdating ? 'Updating...' : 'Disable All'}
          </PanelButton>
        </FieldControlRow>
      </FieldSection>
    </div>
  );
};

export default FieldsContent;
